import type { ReadSourceRef } from "./chat-sources.js";

export interface StarterPrompt {
  text: string;
  template?: boolean;
}

export interface StarterPromptGroup {
  key: string;
  emoji: string;
  label: string;
  description: string;
  prompts: StarterPrompt[];
}

export type StarterPromptLibrarySource = "defaults" | "managed";

export interface StarterPromptLibraryData {
  source: StarterPromptLibrarySource;
  groups: StarterPromptGroup[];
}

export const STARTER_PROMPT_GROUPS: readonly StarterPromptGroup[] = [
  {
    key: "scorecard",
    emoji: "📊",
    label: "Scorecard",
    description:
      "Rank {RD} against the Always list — at a point in time or over their whole record.",
    prompts: [
      {
        text: "Give me a ranking scorecard — how is {RD} doing right now, based on our Always list?",
      },
      {
        text: "Without specific patient-session examples, give me a high-level overview of {RD}'s Always list so I can see their success over time. Rank it like a scorecard.",
      },
    ],
  },
  {
    key: "trends",
    emoji: "📈",
    label: "Trends",
    description:
      "What's changing — and what keeps showing up — across the sessions on file.",
    prompts: [
      { text: "How has {RD} changed over their recent sessions?" },
      { text: "How did {RD}'s sessions this week compare with last week?" },
      {
        text: "What's improved for {RD} in the last month — and what still keeps showing up?",
      },
      { text: "What's the most consistent issue across {RD}'s sessions?" },
    ],
  },
  {
    key: "meeting-prep",
    emoji: "🗒️",
    label: "Meeting prep",
    description: "Walk into supervision with an agenda instead of raw data.",
    prompts: [
      {
        text: "Generate talking points for our next supervision meeting with {RD}.",
      },
      {
        text: "I don't want to overwhelm {RD}. What is the ONE thing they should work on, the ONE thing I should positively reinforce, and their #1 biggest issue? Three bullets only.",
      },
    ],
  },
  {
    key: "coaching",
    emoji: "🎭",
    label: "Coaching",
    description: "Turn real session moments into practice material.",
    prompts: [
      {
        text: "Create a role-play based on actual situations from {RD}'s sessions, with direct quotes of what they said, so I can coach them on it.",
      },
      {
        text: "Find the moment in {RD}'s sessions where they most needed coaching on [topic], and script how I should walk them through it.",
      },
    ],
  },
  {
    key: "full-analysis",
    emoji: "⚡",
    label: "Full analysis",
    description:
      "A guided sequence — five questions answered in order, numbered for follow-ups like 'examples of #3.'",
    prompts: [
      {
        text: "Run a thorough analysis of {RD}. Answer these in order, numbering each answer: 1) How are they scoring against the Always list overall? 2) What's improving across sessions? 3) What's the most consistent issue? 4) What should we work on next and why? 5) What talking points should I bring to our next supervision meeting?",
      },
    ],
  },
];

export function rdFirstName(fullName: string): string {
  const trimmed = fullName.trim();
  return trimmed.split(/\s+/)[0] || fullName;
}

export function interpolateRd(text: string, firstName: string): string {
  // Function replacer: a name containing `$` must insert literally, not act
  // as a replacement pattern.
  return text.replaceAll("{RD}", () => firstName);
}

/**
 * Prompts with [bracketed slots] like "[topic]" are edit-first templates: the
 * New chat UI badges them so supervisors fill the slot before sending. This
 * predicate is the single source of truth — the UI derives the badge from the
 * text, and the server derives the stored `template` column from it on save;
 * the flag is never authored by hand.
 */
export function promptHasSlot(text: string): boolean {
  return /\[[^\]]+\]/.test(text);
}

export function sourceStarterPrompts(
  sourceType: ReadSourceRef["type"],
): string[] {
  if (sourceType === "form") {
    return [
      "What stands out in this form?",
      "What should I focus on in supervision?",
      "How does this compare with recent scorecards?",
    ];
  }
  if (sourceType === "observation") {
    return [
      "What coaching follow-up does this observation suggest?",
      "Is this part of a broader pattern?",
    ];
  }
  if (sourceType === "meeting_note") {
    return [
      "What did we agree to in this meeting?",
      "What action items should I follow up on?",
    ];
  }
  return [
    "What stands out in this scorecard?",
    "What should I reinforce in supervision?",
  ];
}
